/**
 * Canonical share links for a genie. One place decides what a shared URL looks
 * like so Voice, the mobile app and the QR/print assets never drift apart.
 *
 * Two links, two jobs:
 *
 *  - Talk link  — `/live-agent/:urlName`. The public front door. Opens the web
 *    conversation, and (because helpgenie.ai declares `/live-agent/*` in its
 *    apple-app-site-association) opens the native app when it's installed.
 *    `?ref=share` is a *receive intent*: the app reads it and saves the genie
 *    into My Genies while the conversation starts. See the mobile app's
 *    `isSharedGenieReceiveIntent` / `useClaimSharedGenie`.
 *
 *  - Keep link  — `/keep?slug=…&agent_id=…`. The app-first link. On a phone it
 *    hands straight to the native `/keep` route, which writes the genie into
 *    the recipient's collection and then drops them into the conversation. With
 *    no app installed it lands on the web `/keep` page, which offers the store
 *    and a browser conversation instead of dead-ending.
 *
 * Both links are safe to hand to a stranger only when the genie is public —
 * a private genie still needs an access grant, exactly as before.
 */
import { SITE_URL } from "@/config/site";

/** Query marker the mobile app reads as "save this genie to My Genies". */
export const SHARE_RECEIVE_REF = "share";

export interface GenieShareTarget {
  /** `agents.url_name` (or `agent_pages.url_name` — DEPRECATED: agent_pages table is obsolete and scheduled for deletion) — the public slug. */
  urlName?: string | null;
  /** `agents.id`. Lets the app claim without a slug round-trip. */
  agentId?: string | null;
  /** Display name, purely so the app can render it before the fetch lands. */
  name?: string | null;
}

const resolveOrigin = (origin?: string): string => {
  if (origin) return origin.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return SITE_URL;
};

/**
 * The public conversation link. `receiveIntent` appends `?ref=share` so an
 * install of the app also keeps the genie; leave it off for links that should
 * only ever start a conversation (embeds, previews).
 */
export const buildGenieTalkUrl = (
  urlName: string | null | undefined,
  options: { receiveIntent?: boolean; origin?: string } = {},
): string | null => {
  const slug = urlName?.trim();
  if (!slug) return null;

  const url = new URL(
    `/live-agent/${encodeURIComponent(slug)}`,
    resolveOrigin(options.origin),
  );
  if (options.receiveIntent) url.searchParams.set("ref", SHARE_RECEIVE_REF);
  return url.toString();
};

/**
 * The "put this genie in their app" link. Needs at least a slug or an agent id
 * — the mobile `/keep` route can resolve from either.
 */
export const buildGenieKeepUrl = (
  target: GenieShareTarget,
  options: { origin?: string } = {},
): string | null => {
  const slug = target.urlName?.trim();
  const agentId = target.agentId?.trim();
  if (!slug && !agentId) return null;

  const url = new URL("/keep", resolveOrigin(options.origin));
  if (slug) url.searchParams.set("slug", slug);
  if (agentId) url.searchParams.set("agent_id", agentId);
  const name = target.name?.trim();
  if (name) url.searchParams.set("name", name);
  url.searchParams.set("ref", SHARE_RECEIVE_REF);
  return url.toString();
};

/** Human-readable form of the talk link, for the share bars. */
export const formatGenieShareLabel = (
  urlName: string | null | undefined,
): string | null => {
  const slug = urlName?.trim();
  return slug ? `helpgenie.ai/${slug}` : null;
};
